
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { uploadApi } from '../api';
import { useAuth } from '../context/AuthContext';

function CreateListing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    condition: 'new',
    category: 'electronics',
    subcategory: '',
    price: '',
    status: 'active'
  });

  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const categories = [
    { name: 'Vehicles', value: 'vehicles' },
    { name: 'Property Rentals', value: 'property-rentals' },
    { name: 'Apparel', value: 'apparel' },
    { name: 'Classifieds', value: 'classifieds' },
    { name: 'Electronics', value: 'electronics' },
    { name: 'Entertainment', value: 'entertainment' },
    { name: 'Family', value: 'family' },
    { name: 'Free Stuff', value: 'free-stuff' },
    { name: 'Outdoor Living', value: 'outdoor-living' },
    { name: 'Crafts', value: 'crafts' },
    { name: 'Home Decor', value: 'home-decor' },
    { name: 'Building Materials', value: 'building-materials' },
    { name: 'Property Sales', value: 'property-sales' },
    { name: 'Musical Instruments', value: 'musical-instruments' },
    { name: 'Office Equipment', value: 'office-equipment' },
    { name: 'Pets', value: 'pets' },
    { name: 'Sporting Goods', value: 'sporting-goods' },
    { name: 'Toys and Games', value: 'toys-and-games' },
    { name: 'Events', value: 'events' },
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handlePhotoUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Validate photo count
    if (photos.length + selectedFiles.length > 12) {
      setErrors({ ...errors, photos: 'Maximum 12 photos allowed' });
      return;
    }

    // Validate each file
    for (const file of selectedFiles) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/gif', 'image/png', 'image/bmp'];
      if (!validTypes.includes(file.type)) {
        setErrors({ 
          ...errors, 
          photos: `Invalid file type: ${file.name}. Only JPG, PNG, GIF, BMP allowed.` 
        });
        return;
      }

      const maxSize = 12 * 1024 * 1024; // 12MB
      if (file.size > maxSize) {
        setErrors({ 
          ...errors, 
          photos: `File ${file.name} exceeds 12MB limit.` 
        });
        return;
      }
    }

    setPhotos([...photos, ...selectedFiles]);
    setErrors({ ...errors, photos: '' });
  };

  const handleRemovePhoto = (index) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
    if (newPhotos.length < 12 && errors.photos) {
      setErrors({ ...errors, photos: '' });
    }
  };

  // Drag-and-drop reordering
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIdx = dragIndex;
    if (dragIdx === null || dragIdx === dropIndex) return;

    const newPhotos = [...photos];
    const draggedItem = newPhotos[dragIdx];
    newPhotos.splice(dragIdx, 1);
    newPhotos.splice(dropIndex, 0, draggedItem);
    setPhotos(newPhotos);
    setDragIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.price || formData.price < 0) newErrors.price = 'Valid price is required (or leave blank for free items)';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Validate description length
    if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSubmit = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSubmit.append(key, formData[key]);
      });

      // Append photos
      photos.forEach(photo => {
        formDataToSubmit.append('images', photo);
      });

      const response = await uploadApi.post('/api/listings', formDataToSubmit);

      if (response.data.success) {
        alert('Listing created successfully!');
        navigate(`/listings/${response.data.data._id}`);
      } else {
        setErrors({ submit: response.data.message });
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create listing. Please try again.';
      setErrors({ ...errors, submit: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Create New Listing</h1>

      {errors.submit && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline">{errors.submit}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            maxLength={80}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary focus:border-primary ${errors.title ? 'border-red-400' : 'border-gray-300'}`}
            placeholder="e.g., Vintage Camera for Sale"
          />
          <p className="text-xs text-gray-500 mt-1">{formData.title.length}/80 characters</p>
          {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={6}
            maxLength={2000}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary focus:border-primary ${errors.description ? 'border-red-400' : 'border-gray-300'}`}
            placeholder="Describe your item in detail. Include condition, features, and any relevant information."
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">{formData.description.length}/2000 characters</p>
          {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
        </div>

        {/* Price */}
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
            Price ($) (optional for free items)
          </label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            min="0"
            step="0.01"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary focus:border-primary ${errors.price ? 'border-red-400' : 'border-gray-300'}`}
            placeholder="e.g., 150 or leave blank for free"
          />
          {errors.price && <p className="text-red-600 text-sm mt-1">{errors.price}</p>}
        </div>

        {/* Condition */}
        <div>
          <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-1">
            Condition *
          </label>
          <select
            id="condition"
            name="condition"
            value={formData.condition}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
          >
            <option value="new">New - Never used, in original packaging</option>
            <option value="used">Used - Previously owned, still functional</option>
            <option value="non-functional">Non-Functional - Not working as intended</option>
          </select>
        </div>

        {/* Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700 mb-1">
              Subcategory (optional)
            </label>
            <input
              type="text"
              id="subcategory"
              name="subcategory"
              value={formData.subcategory}
              onChange={handleInputChange}
              maxLength={50}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="e.g., Cameras, Laptops, etc."
            />
          </div>
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Photos (max 12, up to 12MB each) *
          </label>

          {photos.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-4">
              {photos.map((photo, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`relative group cursor-move rounded-lg overflow-hidden transition-all ${
                    dragIndex === index 
                      ? 'opacity-50 ring-2 ring-primary scale-95' 
                      : 'hover:ring-2 hover:ring-gray-300'
                  }`}
                >
                  <div className="absolute top-1 left-1 bg-black/60 text-white text-xs w-5 h-5 flex items-center justify-center rounded z-10">
                    {index + 1}
                  </div>
                  <img 
                    src={URL.createObjectURL(photo)} 
                    alt={`Preview ${index + 1}`} 
                    className="w-full h-20 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 z-10"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="block w-full cursor-pointer">
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.bmp"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.photos ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-primary'}`}>
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v8" />
              </svg>
              <p className="mt-2 text-sm text-gray-600">Click to upload photos</p>
              <p className="text-xs text-gray-500 mt-1">{photos.length}/12 photos uploaded</p>
            </div>
          </label>

          {errors.photos && (
            <p className="text-red-600 text-sm mt-2">{errors.photos}</p>
          )}

          {!errors.photos && (
            <p className="text-xs text-gray-500 mt-2">
              Supported formats: JPG, PNG, GIF, BMP. Drag and drop to reorder — the first image becomes the thumbnail.
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Link 
            to="/listings" 
            className="text-gray-700 hover:text-primary font-medium"
          >
            ← Cancel
          </Link>

          <button
            type="submit"
            disabled={submitting || photos.length === 0}
            className={`px-8 py-3 rounded-md font-semibold text-white ${
              submitting || photos.length === 0 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary hover:bg-blue-800 shadow-md'
            } transition-colors`}
          >
            {submitting ? 'Creating Listing...' : 'Create Listing'}
          </button>
        </div>
      </form>

      {/* Preview Section */}
      <div className="mt-8 bg-gray-50 p-6 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Listing Preview</h2>

        {formData.title && (
          <>
            <h3 className="font-bold text-lg">{formData.title}</h3>
            <p className="text-secondary font-semibold">${formData.price || 'Free'}</p>
            <div className="mt-4">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">{formData.condition}</span>
              <span className="ml-2 bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm">{categories.find(c => c.value === formData.category)?.name || 'Category'}</span>
            </div>
          </>
        )}

        {photos.length > 0 && (
          <div className="mt-4">
            <p className="text-gray-600 mb-2">Photos:</p>
            <div className="grid grid-cols-3 gap-2">
              {photos.slice(0, 6).map((photo, index) => (
                <img 
                  key={index} 
                  src={URL.createObjectURL(photo)} 
                  alt={`Preview ${index + 1}`} 
                  className="w-full h-24 object-cover rounded"
                />
              ))}
            </div>
          </div>
        )}

        {!formData.title && (
          <p className="text-gray-500 italic">Fill out the form above to see your listing preview</p>
        )}
      </div>
    </div>
  );
}

export default CreateListing;
